import { Stack, Box, LinearProgress, IconButton } from '@mui/material'
import { Dropzone, EFButton, EFTypography } from '~/components'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '~/app/hooks'
import CloseIcon from '@mui/icons-material/Close'
import { ALL_NS } from '~/i18n/namespaces'
import { currentTheme } from '~/redux/features'
import classNames from 'classnames/bind'
import style from './ImportFilePopup.module.scss'
import { Dispatch, SetStateAction } from 'react'

const cx = classNames.bind(style)

interface IImportFileComponentProps {
  files: File[]
  setFiles: Dispatch<SetStateAction<File[]>>
  isLoading: boolean
  progress: number
  uploadingFileCount: number | undefined
  fileTypes: string[]
  multiple?: boolean
  typeName: string | undefined
}

export default function ImportFileComponent({
  files,
  setFiles,
  isLoading,
  progress,
  uploadingFileCount,
  fileTypes,
  multiple = false,
  typeName
}: IImportFileComponentProps) {
  const { t } = useTranslation(ALL_NS)
  const theme = useAppSelector(currentTheme)

  function concatUniqueFiles(array1: File[], array2: File[]): File[] {
    const uniqueFiles: File[] = []

    const existingFileNames = new Set<string>()
    for (const file of array1) {
      const fileName = file.name
      if (!existingFileNames.has(fileName)) {
        existingFileNames.add(fileName)
        uniqueFiles.push(file)
      }
    }

    for (const file of array2) {
      const fileName = file.name
      if (!existingFileNames.has(fileName)) {
        existingFileNames.add(fileName)
        uniqueFiles.push(file)
      }
    }

    return uniqueFiles
  }

  const handleRemoveFile = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    file: File
  ) => {
    event.stopPropagation()
    const modifiedFiles = files.filter(item => item.name !== file.name)
    setFiles(modifiedFiles)
  }

  const handleFileUpload = (filesChoosed: File[]) => {
    // Handle the uploaded file in the parent component
    if (!multiple) {
      setFiles(filesChoosed)
      return
    }
    const uniqueFiles = concatUniqueFiles(files, filesChoosed)
    if (uniqueFiles) setFiles(uniqueFiles)
  }

  if (isLoading)
    return (
      <Stack
        direction='column'
        justifyContent='center'
        alignItems='center'
        width='100%'
        className={cx('wrapper-import-file', theme)}
      >
        <EFTypography
          fontWeight='bold'
          mb='1rem'
          fontSize='var(--fs-md)'
        >
          {progress < 100
            ? t(files.length > 1 ? 'UploadingFiles' : 'UploadingFile') +
              ` (${uploadingFileCount}/${files.length})`
            : t(files.length > 1 ? 'ProcessingFiles' : 'ProcessingFile') +
              ` (${uploadingFileCount}/${files.length})`}
        </EFTypography>
        <Stack
          display='block'
          maxHeight={300}
          overflow='auto'
          width='100%'
        >
          {files.map(file => (
            <EFTypography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '40rem'
              }}
              noWrap
              key={file.name}
            >
              {file.name}
            </EFTypography>
          ))}
        </Stack>
        <Box
          pt={3}
          sx={{ width: '80%' }}
        >
          <LinearProgress
            color='success'
            variant={progress < 100 ? 'determinate' : 'indeterminate'}
            value={progress}
          />
        </Box>
      </Stack>
    )
  return (
    <Dropzone
      fileTypes={fileTypes}
      onFileUpload={handleFileUpload}
      multiple={multiple}
    >
      <Stack className={cx('wrapper-import-file', theme)}>
        {files.length > 0 ? (
          <Stack
            maxHeight={300}
            overflow='auto'
            width='100%'
          >
            <EFTypography
              fontSize='var(--fs-md)'
              fontWeight={500}
            >
              {t(files.length > 1 ? 'ConfirmUploadMultiple' : 'ConfirmUpload')}
            </EFTypography>
            <Stack my={1}>
              {files.map(file => (
                <Stack
                  key={file.name}
                  direction='row'
                  alignItems='center'
                  justifyContent='center'
                >
                  <EFTypography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '30rem',
                      mr: 0.5
                    }}
                    noWrap
                  >
                    {file.name}
                  </EFTypography>
                  <IconButton
                    id='btn-close'
                    size='small'
                    title='Remove file'
                    className={`small-icon ${theme}`}
                    onClick={event => handleRemoveFile(event, file)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <EFButton
              id='addFilesBtn'
              sx={{ mt: 0.5 }}
            >
              {t('Add Files')}
            </EFButton>
          </Stack>
        ) : (
          <div>
            <EFTypography
              fontWeight={600}
              mb={2}
              fontSize='var(--fs-md)'
            >
              {t('Drop_tk0_HereToUploadOr', { 0: typeName })}
            </EFTypography>
            <EFButton
              id='browseFileBtn'
              sx={{ my: '1rem' }}
            >
              {t('Browse')}
            </EFButton>

            <EFTypography
              fontStyle='italic'
              pt={2}
              fontSize='0.9rem !important'
            >
              {t('AcceptableFileExtensionsAre_tk0_', {
                0: ': .' + fileTypes.join(', .')
              })}
            </EFTypography>
          </div>
        )}
      </Stack>
    </Dropzone>
  )
}
